import React, { lazy, useEffect, useMemo, useState } from "react";
import {
  matchPath,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Sidebar from "style-guide/component/Sidebar";
import {
  AppstoreOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  FileOutlined,
  HistoryOutlined,
  HomeOutlined,
  LineChartOutlined,
  MenuOutlined,
  ProjectOutlined,
} from "@ant-design/icons";
import Button from "style-guide/component/Button";
import { resetVendorEvaluation } from "../../redux/slice/rfpModuleSlice/summary";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { resetUserProfileData } from "../../redux/slice/rfpModuleSlice/application";
import Modal from "style-guide/component/Modal";
import { getCookie } from "utils/utils";
import CreditsModal from "../pricing/Credits";
import { MdSupportAgent } from "react-icons/md";
import UpgradeModal from "components/UpgradeModal";
import { useAccessControl } from "hooks/useAccessControl";

const ChatbotButton = lazy(() => import("components/chatbot"));

const ROUTES = [
  "/rfp-defined",
  "/generate-report",
  "/completed-analysis/:rfpId",
  "/build-scratch",
  "/build-scratch/eval",
  "/analyze-job",
  "/analysed-application/:applicationId",
];

const hrSidebarMenu = [
  {
    title: "Home",
    icon: <HomeOutlined />,
    path: "/home",
  },
  {
    title: "Analysed Application",
    icon: <HistoryOutlined />,
    path: "/analysed-application",
  },
  {
    title: "Deleted Applications",
    icon: <DeleteOutlined />,
    // path: "/deleted-applications",
  },
  {
    title: "Open Application",
    icon: <LineChartOutlined />,
    path: "/open-application",
  },
];

const rfpSidebarMenu = [
  {
    title: "Home",
    icon: <HomeOutlined />,
    path: "/home",
  },
  {
    title: "Dashboard",
    icon: <AppstoreOutlined />,
    path: "/dashboard",
  },
  {
    title: "Reports",
    icon: <FileOutlined />,
    // path: "/reports",
  },
  {
    title: "Created RFPs",
    icon: <ProjectOutlined />,
    path: "/created-rfp",
  },
  {
    title: "Completed Analysis",
    icon: <HistoryOutlined />,
    path: "/completed-analysis",
  },
  {
    title: "Deleted Analysis",
    icon: <DeleteOutlined />,
    path: "/deleted-analysis",
  },
  {
    title: "Open Projects",
    icon: <LineChartOutlined />,
    path: "/open-projects",
  },
  {
    title: "Billing",
    icon: <CreditCardOutlined />,
    path: "/billing",
  },
  {
    title: "Help & Support",
    icon: <MdSupportAgent />,
    path: "/help-support",
  },
];

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLogoutPopupOpen, setIsLogoutPopupOpen] = useState(false);
  const [collapseSidebar, setCollapseSidebar] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);

  const isAuthenticated = Boolean(getCookie("token"));
  const { isAdmin } = useAccessControl();
  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );

  const userVertical = userProfile?.userDetails[0]?.vertical;

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const isMatched = ROUTES.some((routePattern) =>
      matchPath({ path: routePattern, end: true }, pathname)
    );
    setCollapseSidebar(isMatched);
  }, [pathname]);

  const handleContentClick = () => {
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const toggleLogoutPopup = () => {
    setIsLogoutPopupOpen((prev) => !prev);
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
  };

  const handleUpgradePlan = () => {
    setIsUpgradeModalOpen(false);
    navigate("/upgrade");
  };

  const handleBuyCredits = () => {
    setIsUpgradeModalOpen(false);
    setIsCreditsModalOpen(true);
  };

  const handleEnterpriseClick = () => {
    setIsEnterpriseModalOpen(true);
  };

  const handleLogout = async () => {
    document.cookie = "rfp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie =
      "session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    localStorage.clear();
    dispatch(resetUserProfileData());
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }

    dispatch(resetVendorEvaluation());
    navigate("/");
  };

  const filteredSidebarMenu = useMemo(() => {
    return !isAdmin
      ? rfpSidebarMenu.filter((item) => !["Dashboard"].includes(item.title))
      : rfpSidebarMenu;
  }, [isAdmin]);

  return (
      <div className="flex h-screen">
        {/* Sidebar (Fixed) */}
        {isAuthenticated && !token && (
          <div
            className={`fixed md:relative top-0 left-0 h-full ${collapseSidebar ? "w-[80px]" : "w-[256px]"}  bg-gray-800 z-20 transition-transform duration-300 md:translate-x-0 ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <Sidebar
              toggleLogoutPopup={toggleLogoutPopup}
              menuItems={userVertical === "rfp" ? filteredSidebarMenu : hrSidebarMenu}
              collapseSidebar={collapseSidebar}
              onUpgradeClick={handleUpgradeClick}
              onEnterpriseClick={handleEnterpriseClick}
            />
          </div>
        )}

        {/* Mobile Sidebar Toggle */}
        <button
          onClick={toggleSidebar}
          className="md:hidden absolute top-4 left-4 z-30 p-2 text-black rounded-full"
        >
          <MenuOutlined />
        </button>

        {/* Main Content (Scrollable) */}
        <main
          id="main"
          className="flex-1 h-screen overflow-auto py-8 px-2 lg:px-8 transition-all duration-300"
          onClick={handleContentClick}
        >
          <Outlet />
        </main>

        {/* Logout Popup */}
        <Modal
          isOpen={isLogoutPopupOpen}
          onClose={() => setIsLogoutPopupOpen(false)}
          title="Are you sure you want to logout?"
          size="md"
          blurBackdrop
          showCloseIcon={false}
          footer={
            <div className="flex flex-row gap-2 justify-end">
              <Button
                type="Default"
                buttonText="Cancel"
                onClick={toggleLogoutPopup}
              />
              <Button
                type="Primary"
                buttonText="Logout"
                onClick={handleLogout}
              />
            </div>
          }
        />

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          handleBuyCredits={handleBuyCredits}
          handleUpgradePlan={handleUpgradePlan}
        />

        {/* Sidebar Overlay for Mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black opacity-30 md:hidden z-10"
            onClick={toggleSidebar}
          />
        )}

        {/* Credits Modal */}
        <CreditsModal
          isOpen={isCreditsModalOpen}
          onClose={() => setIsCreditsModalOpen(false)}
        />

        {/* Enterprise Modal */}
        <Modal
          isOpen={isEnterpriseModalOpen}
          onClose={() => setIsEnterpriseModalOpen(false)}
          title="Reports Feature"
          size="md"
          blurBackdrop
          showCloseIcon
          footer={
            <div className="flex flex-row gap-2 justify-end">
              <Button
                type="Default"
                buttonText="Close"
                onClick={() => setIsEnterpriseModalOpen(false)}
              />
            </div>
          }
        >
          <div className="text-start py-4">
            <p className="text-gray-600 mb-4">
              The Reports feature is available for Enterprise Customers Only.
            </p>
          </div>
        </Modal>

        {/* Chatbot Button - Available on specific routes only */}
        {isAuthenticated && (
          <ChatbotButton
            position="bottom-right"
            allowedRoutes={["/completed-analysis/:rfpId"]}
          />
        )}
      </div>
  );
};

export default DashboardLayout;
